import React, { useMemo } from "react";
import {
  Alert,
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { videoPlayerService } from "../../services/video-player/VideoPlayerService";
import { translationService } from "../../services/translation/TranslationService";
import { AnalysisDetection, GT_MATCH_TOLERANCE } from "./analysisDetections";

const t = (key: string) => translationService.translate(key);

type RowStatus = "unchanged" | "edited" | "added" | "removed";

interface ComparedRow {
  key: string;
  status: RowStatus;
  original?: AnalysisDetection;
  approved?: AnalysisDetection;
}

interface DetectionSourceComparisonProps {
  original: AnalysisDetection[];
  approved: AnalysisDetection[];
}

const formatTime = (totalSeconds?: number): string => {
  if (totalSeconds === undefined) return "—";
  const s = Math.max(0, Math.round(totalSeconds));
  return `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, "0")}`;
};

const describe = (d?: AnalysisDetection): string => {
  if (!d) return "—";
  return [
    d.mainCode,
    d.meters > 0 ? `${d.meters.toFixed(2)} m` : undefined,
    formatTime(d.timestamp),
    d.c1 ? `C1 ${d.c1}` : undefined,
    d.c2 ? `C2 ${d.c2}` : undefined,
    d.accuracy > 0 ? `${Math.round(d.accuracy * 100)}%` : undefined,
  ]
    .filter(Boolean)
    .join("  ·  ");
};

// Two rows describe the same finding when the reviewer left the code and the position
// essentially alone; anything else counts as a correction worth showing.
const sameFinding = (a: AnalysisDetection, b: AnalysisDetection): boolean =>
  a.mainCode === b.mainCode &&
  Math.abs(a.meters - b.meters) < 0.005 &&
  Math.round(a.timestamp) === Math.round(b.timestamp) &&
  (a.c1 ?? "") === (b.c1 ?? "") &&
  (a.c2 ?? "") === (b.c2 ?? "");

const statusChip = (status: RowStatus) => {
  switch (status) {
    case "added":
      return <Chip size="small" color="success" variant="outlined" label={t("comparison.detection-source.status.added")} />;
    case "edited":
      return <Chip size="small" color="warning" variant="outlined" label={t("comparison.detection-source.status.edited")} />;
    case "removed":
      return <Chip size="small" color="error" variant="outlined" label={t("comparison.detection-source.status.removed")} />;
    default:
      return <Chip size="small" variant="outlined" label={t("comparison.detection-source.status.unchanged")} />;
  }
};

/**
 * What the reviewer changed: the raw analysis output next to the approved list.
 *
 * This deliberately sits inside the Video Analysis tab rather than in the Comparison tab —
 * that tab compares OCR against AI, which is a different question and is meaningless on an
 * analysis-only file.
 */
const DetectionSourceComparison: React.FC<DetectionSourceComparisonProps> = ({
  original,
  approved,
}) => {
  const rows = useMemo<ComparedRow[]>(() => {
    const takenOriginals = new Set<number>();
    const out: ComparedRow[] = [];

    approved.forEach((a, ai) => {
      // Nearest same-code original within the usual matching tolerance. Nearest rather than
      // first, so a reviewer nudging one of two close detections doesn't steal the other's
      // counterpart and report both as edited.
      let bestIndex = -1;
      let bestDelta = Infinity;
      original.forEach((o, oi) => {
        if (takenOriginals.has(oi) || o.mainCode !== a.mainCode) return;
        const delta = Math.abs(o.meters - a.meters);
        if (delta <= GT_MATCH_TOLERANCE && delta < bestDelta) {
          bestDelta = delta;
          bestIndex = oi;
        }
      });

      if (bestIndex === -1) {
        out.push({ key: `added-${ai}`, status: "added", approved: a });
        return;
      }
      takenOriginals.add(bestIndex);
      const o = original[bestIndex];
      out.push({
        key: `pair-${bestIndex}-${ai}`,
        status: sameFinding(o, a) ? "unchanged" : "edited",
        original: o,
        approved: a,
      });
    });

    // Anything the model proposed that never made it into the approved list was dismissed.
    original.forEach((o, oi) => {
      if (takenOriginals.has(oi)) return;
      out.push({ key: `removed-${oi}`, status: "removed", original: o });
    });

    const positionOf = (r: ComparedRow) => (r.approved ?? r.original)?.meters ?? 0;
    return out.sort((a, b) => positionOf(a) - positionOf(b));
  }, [original, approved]);

  const counts = rows.reduce<Record<RowStatus, number>>(
    (acc, r) => ({ ...acc, [r.status]: acc[r.status] + 1 }),
    { unchanged: 0, edited: 0, added: 0, removed: 0 }
  );

  if (rows.length === 0) {
    return <Alert severity="info">{t("comparison.va-detections.empty")}</Alert>;
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5, flexWrap: "wrap" }}>
        <Typography variant="h6">{t("comparison.detection-source.compare-title")}</Typography>
        {(["edited", "added", "removed", "unchanged"] as RowStatus[])
          .filter((s) => counts[s] > 0)
          .map((s) => (
            <Box key={s} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {statusChip(s)}
              <Typography variant="caption" color="text.secondary">
                {counts[s]}
              </Typography>
            </Box>
          ))}
      </Box>

      <Box sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t("comparison.detection-source.status.column")}</TableCell>
              <TableCell>{t("comparison.detection-source.original")}</TableCell>
              <TableCell>{t("comparison.detection-source.approved")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => {
              const jumpTo = (r.approved ?? r.original)?.timestamp;
              return (
                <TableRow
                  key={r.key}
                  hover
                  sx={{ cursor: jumpTo !== undefined ? "pointer" : "default" }}
                  onClick={() => jumpTo !== undefined && videoPlayerService.jumpTo(jumpTo)}
                >
                  <TableCell>{statusChip(r.status)}</TableCell>
                  <TableCell
                    sx={{
                      color: r.status === "removed" ? "error.main" : "text.primary",
                      textDecoration: r.status === "removed" ? "line-through" : "none",
                    }}
                  >
                    {describe(r.original)}
                  </TableCell>
                  <TableCell sx={{ color: r.status === "added" ? "success.main" : "text.primary" }}>
                    {describe(r.approved)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
};

export default DetectionSourceComparison;
